// src/features/my-work/logic.ts
// Framework-agnostic My Work logic — ZERO React imports.
// Pure TypeScript functions for the personal work hub.

import type { Task, TaskStatus } from '@core/types/task';
import type { MyWorkTab, UrgencyGroup, FocusSegment } from './types';

import {
  getTaskStatus,
  getTaskDueDate,
  sortTasksByPriority,
  taskStatusMap,
} from '@/features/task/logic';

// ---------------------------------------------------------------------------
// Focus stats
// ---------------------------------------------------------------------------

export interface FocusStats {
  overdueCount: number;
  dueThisWeekCount: number;
  awaitingReviewCount: number;
  inProgressCount: number;
}

export const TERMINAL_STATUSES = new Set([
  'TASK_STATUS_DONE',
  'TASK_STATUS_CANCELLED',
  'TASK_STATUS_ARCHIVED',
]);

/**
 * Compute summary stats for the FocusBar from a list of tasks.
 * Only counts active tasks (not done/cancelled/archived).
 */
export function computeFocusStats(tasks: Task[]): FocusStats {
  let overdueCount = 0;
  let dueThisWeekCount = 0;
  let awaitingReviewCount = 0;
  let inProgressCount = 0;

  const now = Date.now();
  const weekFromNow = now + 7 * 24 * 60 * 60 * 1000;

  for (const task of tasks) {
    const status = getTaskStatus(task);
    if (TERMINAL_STATUSES.has(status)) continue;

    const dueDate = getTaskDueDate(task);

    if (status === 'TASK_STATUS_IN_REVIEW') {
      awaitingReviewCount++;
    }

    if (status === 'TASK_STATUS_IN_PROGRESS') {
      inProgressCount++;
    }

    if (dueDate) {
      try {
        const dueTime = new Date(dueDate).getTime();
        if (isNaN(dueTime)) continue;
        if (dueTime < now) {
          overdueCount++;
        } else if (dueTime <= weekFromNow) {
          dueThisWeekCount++;
        }
      } catch {
        // skip unparseable dates
      }
    }
  }

  return { overdueCount, dueThisWeekCount, awaitingReviewCount, inProgressCount };
}

// ---------------------------------------------------------------------------
// Urgency grouping
// ---------------------------------------------------------------------------

export interface UrgencyGroupConfig {
  id: UrgencyGroup;
  labelKey: string;
  variant: string;
}

export const URGENCY_GROUPS: UrgencyGroupConfig[] = [
  { id: 'overdue', labelKey: 'myWork.group.overdue', variant: 'error' },
  { id: 'due_this_week', labelKey: 'myWork.group.dueThisWeek', variant: 'warning' },
  { id: 'in_progress', labelKey: 'myWork.group.inProgress', variant: 'primary' },
  { id: 'upcoming', labelKey: 'myWork.group.upcoming', variant: 'default' },
  { id: 'no_date', labelKey: 'myWork.group.noDate', variant: 'secondary' },
];

/**
 * Group tasks into urgency buckets. Each task ends up in exactly one bucket.
 * Order of precedence:
 *  1. overdue — due_date < now AND status not terminal
 *  2. due_this_week — due_date within 7 days from now
 *  3. in_progress — status IN_PROGRESS or IN_REVIEW (no due date soon)
 *  4. upcoming — has due_date in the future (>7 days)
 *  5. no_date — everything else
 *
 * Tasks within each group are sorted by priority (highest first).
 */
export function groupTasksByUrgency(tasks: Task[]): Record<UrgencyGroup, Task[]> {
  const groups: Record<UrgencyGroup, Task[]> = {
    overdue: [],
    due_this_week: [],
    in_progress: [],
    upcoming: [],
    no_date: [],
  };

  const now = Date.now();
  const weekFromNow = now + 7 * 24 * 60 * 60 * 1000;

  for (const task of tasks) {
    const status = getTaskStatus(task);

    // Skip terminal tasks entirely
    if (TERMINAL_STATUSES.has(status)) continue;

    const dueDate = getTaskDueDate(task);
    let dueTime: number | null = null;

    if (dueDate) {
      try {
        const parsed = new Date(dueDate).getTime();
        if (!isNaN(parsed)) dueTime = parsed;
      } catch {
        // treat as no date
      }
    }

    if (dueTime !== null && dueTime < now) {
      groups.overdue.push(task);
    } else if (dueTime !== null && dueTime <= weekFromNow) {
      groups.due_this_week.push(task);
    } else if (status === 'TASK_STATUS_IN_PROGRESS' || status === 'TASK_STATUS_IN_REVIEW') {
      groups.in_progress.push(task);
    } else if (dueTime !== null) {
      groups.upcoming.push(task);
    } else {
      groups.no_date.push(task);
    }
  }

  // Sort each bucket by priority
  for (const key of Object.keys(groups) as UrgencyGroup[]) {
    groups[key] = sortTasksByPriority(groups[key]);
  }

  return groups;
}

// ---------------------------------------------------------------------------
// Tab → API filter params
// ---------------------------------------------------------------------------

export interface MyWorkTabConfig {
  id: MyWorkTab;
  labelKey: string;
}

export const MY_WORK_TABS: MyWorkTabConfig[] = [
  { id: 'assigned', labelKey: 'myWork.tab.assigned' },
  { id: 'created', labelKey: 'myWork.tab.created' },
  { id: 'watching', labelKey: 'myWork.tab.watching' },
];

/**
 * Return API filter params for the ListMyTasks endpoint based on tab.
 */
export function getMyWorkTabFilter(tab: MyWorkTab): Record<string, boolean> {
  return {
    assigned_to_me: tab === 'assigned',
    created_by_me: tab === 'created',
    watching: tab === 'watching',
  };
}

// ---------------------------------------------------------------------------
// Urgency variant → Tailwind class maps
// ---------------------------------------------------------------------------

export function getUrgencyVariantClasses(variant: string): {
  bg: string;
  text: string;
  border: string;
} {
  switch (variant) {
    case 'error':
      return { bg: 'bg-destructive/10', text: 'text-destructive', border: 'border-destructive/20' };
    case 'warning':
      return { bg: 'bg-yellow-500/10', text: 'text-yellow-600 dark:text-yellow-400', border: 'border-yellow-500/20' };
    case 'primary':
      return { bg: 'bg-primary/10', text: 'text-primary', border: 'border-primary/20' };
    case 'default':
      return { bg: 'bg-muted', text: 'text-foreground', border: 'border-border' };
    case 'secondary':
    default:
      return { bg: 'bg-muted/50', text: 'text-muted-foreground', border: 'border-border' };
  }
}

// ---------------------------------------------------------------------------
// Status filter pills
// ---------------------------------------------------------------------------

export interface StatusFilterOption {
  value: TaskStatus | '';
  labelKey: string;
}

/** "All" + every known task status, in display order. Backed by taskStatusMap
 *  so the pills always match the badges rendered elsewhere in the app. */
export const MY_WORK_STATUS_FILTERS: StatusFilterOption[] = [
  { value: '', labelKey: 'common.all' },
  ...(Object.keys(taskStatusMap) as TaskStatus[])
    .filter(status => status !== 'TASK_STATUS_UNSPECIFIED')
    .map(status => ({ value: status, labelKey: taskStatusMap[status].labelKey })),
];

// ---------------------------------------------------------------------------
// Client-side search / status filtering
// ---------------------------------------------------------------------------

/**
 * Filter an already-fetched task list by status and free-text search.
 *
 * This is a client-side safety net, not just a convenience: ListMyTasks is a
 * GET endpoint and the API client only flattens query params one level deep
 * (see core/api/client.ts buildUrl), so a two-level-deep field like
 * `filters.statuses.statuses[]` may never reach the server even though the
 * request is built correctly (known framework defect framework-giph).
 * `search_query` is one level deep and does reach the server, but re-applying
 * it here is cheap and keeps behavior correct regardless of what the backend
 * actually honored.
 */
export function filterMyWorkTasks(tasks: Task[], statusFilter: string, searchQuery: string): Task[] {
  const status = statusFilter.trim();
  const query = searchQuery.trim().toLowerCase();
  if (!status && !query) return tasks;

  return tasks.filter(task => {
    if (status && getTaskStatus(task) !== status) return false;
    if (query) {
      const title = String((task as any).title ?? '').toLowerCase();
      if (!title.includes(query)) return false;
    }
    return true;
  });
}

// ---------------------------------------------------------------------------
// FocusBar segment filtering
// ---------------------------------------------------------------------------

/**
 * Filter tasks down to the ones matching a FocusBar segment (task 3 — clicking
 * a FocusBar stat now actually narrows the list, not just scrolls to it).
 * Mirrors the predicates in computeFocusStats so counts and filtered results
 * always agree.
 */
export function filterTasksByFocusSegment(tasks: Task[], segment: FocusSegment | null): Task[] {
  if (!segment) return tasks;

  const now = Date.now();
  const weekFromNow = now + 7 * 24 * 60 * 60 * 1000;

  return tasks.filter(task => {
    const status = getTaskStatus(task);
    if (TERMINAL_STATUSES.has(status)) return false;

    if (segment === 'in_review') return status === 'TASK_STATUS_IN_REVIEW';
    if (segment === 'in_progress') return status === 'TASK_STATUS_IN_PROGRESS';

    const dueDate = getTaskDueDate(task);
    let dueTime: number | null = null;
    if (dueDate) {
      try {
        const parsed = new Date(dueDate).getTime();
        if (!isNaN(parsed)) dueTime = parsed;
      } catch {
        // treat as no date
      }
    }
    if (dueTime === null) return false;

    if (segment === 'overdue') return dueTime < now;
    if (segment === 'due_this_week') return dueTime >= now && dueTime <= weekFromNow;
    return true;
  });
}
